/** Writ of ejectment set-out pieces shared by the office and the field app. */
import { useEffect, useState } from 'react';
import { api, ApiError } from './api.ts';
import { ErrorBox, Field, Notice, useAction, useLoad } from './ui.tsx';

/** The agency's time zone; set once the user (or the landlord's link) is loaded. */
export let TZ = 'America/New_York';
export const setAgencyTz = (tz: string | null | undefined) => {
  if (tz) TZ = tz;
};
export const fmtWhen = (v: string | Date, tz = TZ) =>
  new Date(v).toLocaleString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
export const fmtTimeOnly = (v: string | Date, tz = TZ) => new Date(v).toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
export const localDay = (d: Date, tz = TZ) => d.toLocaleDateString('en-CA', { timeZone: tz });
const localHm = (d: Date, tz = TZ) => d.toLocaleTimeString('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit' });
/** Agency local date + time → ISO (works whatever the device's own time zone is). */
export function localToIso(day: string, hm: string, tz = TZ) {
  const want = Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10), +hm.slice(0, 2), +hm.slice(3, 5));
  let t = want;
  for (let i = 0; i < 3; i++) {
    const d = new Date(t);
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(d).map((x) => [x.type, x.value]));
    const got = Date.UTC(+p.year!, +p.month! - 1, +p.day!, +p.hour!, +p.minute!);
    if (got === want) break;
    t += want - got;
  }
  return new Date(t).toISOString();
}

export const OUTCOME_LABEL: Record<string, string> = { executed: 'Set-out done', already_vacated: 'Already moved out', cancelled: 'Cancelled' };

/** Copy / email / text the landlord's link (until an email/text service is connected). */
export function LandlordLinkBox({ link, landlord, when, address }: { link: string; landlord: { name?: string | null; phone?: string | null; email?: string | null }; when: string | Date; address: string }) {
  const url = `${location.origin}${link}`;
  const [copied, setCopied] = useState(false);
  const msg = `The set-out at ${address} is scheduled for ${fmtWhen(when)}. Please confirm you'll be there (or let us know if the tenant has paid or moved out): ${url}`;
  return (
    <div className="link-box">
      <div className="small">Landlord’s link — they confirm the time or ask to cancel (no account needed):</div>
      <div className="row gap-sm wrap">
        <input readOnly value={url} aria-label="Landlord link" onFocus={(e) => e.target.select()} className="grow mono" />
        <button type="button" className="btn sm" onClick={() => navigator.clipboard?.writeText(url).then(() => setCopied(true))}>
          {copied ? '✓ Copied' : 'Copy link'}
        </button>
        {landlord.phone && (
          <a className="btn sm" href={`sms:${landlord.phone.replace(/[^\d+]/g, '')}?&body=${encodeURIComponent(msg)}`}>
            💬 Text it
          </a>
        )}
        <a className="btn sm primary" href={`mailto:${landlord.email ?? ''}?subject=${encodeURIComponent(`Set-out scheduled — ${address}`)}&body=${encodeURIComponent(msg)}`}>
          ✉ Email it
        </a>
      </div>
    </div>
  );
}

/** Schedule or reschedule a set-out: date, time, deputies (with who's free), landlord. */
export function ScheduleDialog({
  paper,
  current,
  minDeputies = 2,
  onClose,
  onDone,
}: {
  paper: { id: string; defendant: string; address: string; plaintiff?: string | null; unlockAt?: string | null };
  current?: any | null;
  minDeputies?: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const start = current ? new Date(current.scheduledFor) : new Date(Math.max(Date.now() + 86400e3, paper.unlockAt ? +new Date(paper.unlockAt) : 0));
  const [day, setDay] = useState(localDay(start));
  const [time, setTime] = useState(current ? localHm(start) : '10:00');
  const [picked, setPicked] = useState<string[]>(current?.deputies?.map((d: any) => d.id) ?? []);
  const [ll, setLl] = useState({ name: current?.landlord?.name ?? paper.plaintiff ?? '', phone: current?.landlord?.phone ?? '', email: current?.landlord?.email ?? '' });
  const [equipment, setEquipment] = useState(current?.equipment ?? '');
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState<{ link: string; when: string } | null>(null);
  const av = useLoad(() => api.get<any>(`/api/setouts/availability?date=${day}`), [day]);
  const a = useAction();
  const err = a.error instanceof ApiError ? a.error : null;
  const dayOff = err?.details?.code === 'SETOUT_DAY_OFF';
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const submit = (ignoreDayOff = false) =>
    a.run(async () => {
      const when = localToIso(day, time);
      const r = await api.post<{ landlordLink: string }>(`/api/papers/${paper.id}/setout`, {
        scheduledFor: when,
        serverIds: picked,
        landlordName: ll.name.trim() || undefined,
        landlordPhone: ll.phone.trim() || undefined,
        landlordEmail: ll.email.trim() || undefined,
        equipment: equipment.trim() || undefined,
        notes: notes.trim() || undefined,
        ignoreDayOff,
      });
      setDone({ link: r.landlordLink, when });
    });
  return (
    <div className="modal-back" role="dialog" aria-label={current ? 'Reschedule set-out' : 'Schedule set-out'}>
      <div className="modal wide">
        <h2>{current ? 'Reschedule' : 'Schedule'} set-out · {paper.defendant}</h2>
        <div className="small muted">{paper.address}</div>
        {done ? (
          <>
            <Notice tone="ok">Set-out {current ? 'moved to' : 'scheduled for'} {fmtWhen(done.when)}. The deputies see it on their phones.</Notice>
            <LandlordLinkBox link={done.link} landlord={ll} when={done.when} address={paper.address} />
            <div className="row gap">
              <button className="btn primary" onClick={onDone}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            {paper.unlockAt && +new Date(paper.unlockAt) > Date.now() && <Notice tone="warn">The 24-hour wait ends {fmtWhen(paper.unlockAt)}.</Notice>}
            <div className="grid2">
              <Field label="Date">
                <input type="date" value={day} onChange={(e) => setDay(e.target.value)} aria-label="Set-out date" />
              </Field>
              <Field label="Time">
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Set-out time" step={900} />
              </Field>
            </div>
            <fieldset className="so-deps">
              <legend>
                Deputies <span className="small muted">— at least {minDeputies}</span>
              </legend>
              {(av.data?.deputies ?? []).map((d: any) => {
                const off = d.schedule?.kind === 'off';
                const near = d.setOuts.filter((s: any) => Math.abs(+new Date(s.at) - +new Date(localToIso(day, time))) < 2 * 3600e3 && s.id !== current?.id);
                return (
                  <label key={d.id} className={`so-dep${off ? ' off' : ''}${near.length ? ' busy' : ''}`}>
                    <input type="checkbox" checked={picked.includes(d.id)} onChange={() => toggle(d.id)} aria-label={d.name} />
                    <span className="grow">
                      <strong>{d.name}</strong> {d.badge && <span className="muted small">{d.badge}</span>}
                    </span>
                    <span className="small">
                      {off ? 'Off this day' : d.schedule?.kind === 'working' ? `Working ${d.schedule.start ?? ''}–${d.schedule.end ?? ''}` : ''}
                      {d.setOuts.length > 0 && ` · set-outs ${d.setOuts.map((s: any) => fmtTimeOnly(s.at)).join(', ')}`}
                      {near.length > 0 && ' · conflict'}
                    </span>
                  </label>
                );
              })}
            </fieldset>
            <div className="grid3">
              <Field label="Landlord">
                <input value={ll.name} onChange={(e) => setLl({ ...ll, name: e.target.value })} aria-label="Landlord name" />
              </Field>
              <Field label="Landlord phone">
                <input value={ll.phone} onChange={(e) => setLl({ ...ll, phone: e.target.value })} aria-label="Landlord phone" />
              </Field>
              <Field label="Landlord email">
                <input type="email" value={ll.email} onChange={(e) => setLl({ ...ll, email: e.target.value })} aria-label="Landlord email" />
              </Field>
            </div>
            <Field label="Equipment / help needed">
              <input value={equipment} onChange={(e) => setEquipment(e.target.value)} placeholder="e.g. Locksmith meeting us, moving crew" />
            </Field>
            <Field label="Notes for the deputies">
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Dog in the yard; tenant was hostile at posting" />
            </Field>
            <ErrorBox error={a.error} />
            <div className="row gap wrap">
              <button className="btn primary" disabled={a.busy || picked.length < minDeputies || !day || !time} onClick={() => submit(false)}>
                {a.busy ? 'Saving…' : current ? 'Reschedule' : 'Schedule set-out'}
              </button>
              {dayOff && (
                <button className="btn warn" disabled={a.busy} onClick={() => submit(true)}>
                  Schedule anyway (call them in)
                </button>
              )}
              <button className="btn ghost" onClick={onClose}>
                Cancel
              </button>
              {picked.length < minDeputies && <span className="small muted">Pick {minDeputies - picked.length} more deput{minDeputies - picked.length > 1 ? 'ies' : 'y'}.</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Shrink a phone photo to a JPEG ≤1600 px before upload. */
export async function photoToJpegBase64(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error('Can’t read that photo'));
      i.src = url;
    });
    const k = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.round(img.naturalWidth * k);
    c.height = Math.round(img.naturalHeight * k);
    c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.82).split(',')[1]!;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Record what happened at the set-out. */
export function ResultForm({ setOut, deputies, onDone, onCancel, big = false }: { setOut: any; deputies: { id: string; name: string }[]; onDone: () => void; onCancel?: () => void; big?: boolean }) {
  const now = new Date();
  const [outcome, setOutcome] = useState<'executed' | 'already_vacated'>('executed');
  const [day, setDay] = useState(localDay(now));
  const [time, setTime] = useState(localHm(now));
  const [present, setPresent] = useState<string[]>(setOut.deputies.map((d: any) => d.id));
  const [locks, setLocks] = useState(true);
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const a = useAction();
  const all = [...setOut.deputies, ...deputies.filter((d) => !setOut.deputies.some((x: any) => x.id === d.id))];
  const save = () =>
    a.run(async () => {
      const ph = await Promise.all(photos.map(async (f, i) => ({ filename: `setout-${i + 1}.jpg`, dataBase64: await photoToJpegBase64(f) })));
      await api.post(`/api/setouts/${setOut.id}/result`, { outcome, at: localToIso(day, time), deputiesPresent: present, locksChanged: locks, notes: notes.trim() || undefined, photos: ph });
      onDone();
    });
  useEffect(() => setPresent(setOut.deputies.map((d: any) => d.id)), [setOut.id]);
  return (
    <div className={`stack so-result${big ? ' big' : ''}`}>
      <div className="seg so-outcome" role="radiogroup" aria-label="What happened">
        {(['executed', 'already_vacated'] as const).map((k) => (
          <button key={k} role="radio" aria-checked={outcome === k} className={outcome === k ? 'active' : ''} onClick={() => setOutcome(k)}>
            {k === 'executed' ? 'Set-out done' : 'Already moved out'}
          </button>
        ))}
      </div>
      <div className="grid2">
        <Field label="Date">
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} aria-label="Result date" />
        </Field>
        <Field label="Time">
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Result time" />
        </Field>
      </div>
      <fieldset className="so-deps">
        <legend>Deputies there</legend>
        {all.map((d: any) => (
          <label key={d.id} className="so-dep">
            <input type="checkbox" checked={present.includes(d.id)} onChange={() => setPresent((p) => (p.includes(d.id) ? p.filter((x) => x !== d.id) : [...p, d.id]))} aria-label={`${d.name} was there`} />
            <span>{d.name}</span>
          </label>
        ))}
      </fieldset>
      <label className="check">
        <input type="checkbox" checked={locks} onChange={(e) => setLocks(e.target.checked)} /> Locks changed / landlord given possession
      </label>
      <Field label="Notes">
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Tenant left peacefully; belongings placed at the curb" />
      </Field>
      <Field label="Photos (optional)">
        <input type="file" accept="image/*" multiple aria-label="Set-out photos" onChange={(e) => setPhotos([...(e.target.files ?? [])])} />
      </Field>
      <ErrorBox error={a.error} />
      <div className="row gap wrap">
        <button className={`btn primary${big ? ' big' : ''}`} disabled={a.busy || !present.length} onClick={save}>
          {a.busy ? 'Saving…' : 'Save result'}
        </button>
        {onCancel && (
          <button className="btn ghost" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

export function ResultDialog({ setOut, title, onClose, onDone }: { setOut: any; title: string; onClose: () => void; onDone: () => void }) {
  const meta = useLoad(() => api.get<any>('/api/meta'));
  return (
    <div className="modal-back" role="dialog" aria-label="Record set-out">
      <div className="modal wide">
        <h2>Record set-out · {title}</h2>
        <ResultForm setOut={setOut} deputies={(meta.data?.servers ?? []).map((s: any) => ({ id: s.id, name: s.display_name }))} onDone={onDone} onCancel={onClose} />
      </div>
    </div>
  );
}
